import { Button, Form, Table } from "antd";
import Icon from "../../components/Icon";
import HrnModal from "../../components/HrnModal";
import validateMessages from "../../utils/validate";
import { LoadingOutlined } from "@ant-design/icons";
import { Actions, SortingHandler } from "../../shared/constants";
import { useEffect, useState } from "react";
import { useCreateModuleMutation, useDeleteModuleMutation, useGetModulesQuery, useSortModulesMutation, useUpdateModuleMutation } from "../../services/module";
import globalUtils from "../../utils/globalUtils";
import tableUtils from "../../utils/tableUtils";
import { ModulesColumn } from "../../shared/table/columns";
import ModuleForm from "./ModuleForm";
import DndSortableContext from "../../components/Table/DndSortableContext";
import { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import MoveableRow from "../../components/Table/MoveableRow";

const Module = () => {
    const [moduleForm] = Form.useForm();
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState("");
    const [editId, setEditId] = useState("");
    const [dataSource, setDataSource] = useState<any[]>([]);
    const [copiedDataSource, setCopiedDataSource] = useState<any[] | null>(null);
    const {
        data: modules,
        isFetching: isModulesFetching,
        isSuccess: isModulesSuccess,
    } = useGetModulesQuery(
        { includeDisabled: true },
        { refetchOnMountOrArgChange: true },
    );

    const [createModule, createModuleResult] = useCreateModuleMutation();
    const [updateModule, updateModuleResult] = useUpdateModuleMutation();
    const [deleteModule] = useDeleteModuleMutation();
    const [sortModules] = useSortModulesMutation();

    useEffect(() => {
        if (isModulesSuccess) {
            setDataSource(modules.data);
        }
    }, [isModulesFetching]);

    const showModal = () => {
        setMode(Actions.CREATE);
        setOpen(true);
    };

    const hideModal = () => {
        setOpen(false);
        moduleForm.resetFields();
        setMode("");
    };

    const onSave = async () => {
        try {
            await moduleForm.validateFields();
        } catch (errorInfo) {
            return;
        }

        const formFields = moduleForm.getFieldsValue();

        if (mode === Actions.CREATE) {
            // ** try catch must need for mutation!
            try {
                await createModule(formFields).unwrap();
                setOpen(false);
                moduleForm.resetFields();
            } catch (e) { }
        } else {
            try {
                await updateModule({ id: editId, body: formFields }).unwrap();
                setOpen(false);
                moduleForm.resetFields();
            } catch (e) { }
        }
    };

    const handleEdit = (record: any) => {
        setMode(Actions.EDIT);
        setOpen(true);
        if (record.id) setEditId(record.id);
        moduleForm.setFieldsValue(record);
    };

    const handleDelete = async (record: any) => {
        try {
            await deleteModule({ id: record.id }).unwrap();
        } catch (e) { }
    };

    const onDragStart = async (event: DragStartEvent) => {
        setCopiedDataSource([...dataSource]);
    };

    const onDragEnd = async ({ active, over }: DragEndEvent) => {
        let sortedData: any[] = [];
        if (active.id !== over?.id) {
            setDataSource((previous) => {
                const activeIndex = previous.findIndex(
                    (i: any) => i.id === active.id,
                );
                const overIndex = previous.findIndex((i: any) => i.id === over?.id);
                sortedData = arrayMove(previous, activeIndex, overIndex);
                return arrayMove(previous, activeIndex, overIndex);
            });
        }
        const sortedIds = sortedData.map((el) => el.id);
        try {
            await sortModules(sortedIds).unwrap();
            setCopiedDataSource(null);
        } catch (error) {
            if (copiedDataSource != null) setDataSource([...copiedDataSource]);
        }
    };

    return (
        <>
            <div className="flex justify-between items-center gap-3 pb-6">
                <div className="text-headline">{"Modules"}</div>
                <Button type="primary" size="large" className="flex-end" onClick={showModal}>
                    <div className="flex items-center gap-1.5">
                        <Icon icon="add" className="h-4.5 w-4.5" />
                        <div>{"Add Module"}</div>
                    </div>
                </Button>
            </div>
            <div className="flex justify-between flex-wrap items-center gap-4 pb-4">
                <div className="flex items-center gap-x-4">
                    <div className="font-medium">
                        {dataSource?.length}{" "}
                        {globalUtils.checkIsSingular(dataSource.length)
                            ? "Module"
                            : "Modules"}
                    </div>
                </div>
            </div>
            <div>
                <HrnModal
                    open={open}
                    title={`${mode === Actions.CREATE ? "Add" : "Edit"} Module`}
                    width={520}
                    isLoading={
                        (createModuleResult.isLoading ||
                            updateModuleResult.isLoading) as boolean
                    }
                    onCancel={hideModal}
                    onSave={onSave}
                >
                    <div className="mt-6">
                        <Form
                            form={moduleForm}
                            layout="vertical"
                            name="moduleForm"
                            validateMessages={validateMessages}
                        >
                            <ModuleForm mode={mode} moduleForm={moduleForm} />
                        </Form>
                    </div>
                </HrnModal>
            </div>
            <div>
                <DndSortableContext dataSource={dataSource} onDragStart={onDragStart} onDragEnd={onDragEnd}>
                    <Table
                        rowKey="id"
                        components={{
                            body: {
                                row: MoveableRow,
                            },
                        }}
                        columns={tableUtils.buildColumns(
                            isModulesSuccess ? dataSource : [],
                            ModulesColumn,
                            tableUtils.editDeleteActionColumns(handleEdit, handleDelete),
                            [SortingHandler],
                            undefined,
                            { serialNo: true }
                        )}
                        dataSource={dataSource}
                        size="middle"
                        className="ant_table"
                        loading={{
                            spinning: isModulesFetching as boolean,
                            indicator: <LoadingOutlined style={{ fontSize: 34 }} />,
                        }}
                        pagination={false}
                    />
                </DndSortableContext>
            </div>
        </>
    );
};

export default Module;
